import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
} from "@store/slices/chart-config-slice";
import {
  DEFAULT_VISUALIZATION_BASE,
  type VisualizationBaseCondition,
} from "@models/VisualizationBase";
import { isVisualizationBaseValid } from "@utils/visualization-base-utils";

export function useVisualizationBase() {
  const dispatch = useAppDispatch();
  const { currentConfig } = useAppSelector(chartConfigSelector);
  const { updateVisualizationBase } = chartConfigDispatcher(dispatch);

  const base = currentConfig.visualizationBase ?? DEFAULT_VISUALIZATION_BASE;

  const enableCustomBase = useCallback(() => {
    updateVisualizationBase({ ...base, enabled: true });
  }, [base, updateVisualizationBase]);

  // Switching to "All eligible respondents" only hides the custom base — name
  // and conditions stay in state (mirrors the Focus Metric single/rollup
  // toggle) so switching back to Custom Base before the widget is actually
  // saved restores everything exactly as configured. The data is only truly
  // gone once the widget is saved while disabled.
  const disableCustomBase = useCallback(() => {
    updateVisualizationBase({ ...base, enabled: false });
  }, [base, updateVisualizationBase]);

  const updateName = useCallback(
    (name: string) => {
      updateVisualizationBase({ ...base, name });
    },
    [base, updateVisualizationBase],
  );

  // Commits a condition built in the builder — appends if new, replaces if editing.
  const saveCondition = useCallback(
    (condition: VisualizationBaseCondition, isEditing: boolean) => {
      updateVisualizationBase({
        ...base,
        conditions: isEditing
          ? base.conditions.map((c) => (c.id === condition.id ? condition : c))
          : [...base.conditions, condition],
      });
    },
    [base, updateVisualizationBase],
  );

  const removeCondition = useCallback(
    (id: string) => {
      updateVisualizationBase({
        ...base,
        conditions: base.conditions.filter((c) => c.id !== id),
      });
    },
    [base, updateVisualizationBase],
  );

  const isValid = isVisualizationBaseValid(base);

  return {
    base,
    isValid,
    setBase: updateVisualizationBase,
    enableCustomBase,
    disableCustomBase,
    updateName,
    saveCondition,
    removeCondition,
  };
}
